import './Rdc.scss'

import { HDSButton } from '@here/hds-react-components'
import { CloseButton } from 'components/App'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import { RouteWaypoint, StateRoute, StateRouteAttributions } from 'data/model/routePlanner'
import { setSelectedRoute } from 'data/reducer/routePlanner'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { toRdcSection } from 'lib/route-planner/formatters'
import React from 'react'
import { formatDistance, formatLocalTimeDuration } from 'utils/formatters'

import { RootState } from '../../data/reducer'
import ArrivalSection from './ArrivalSection'
import BikeElevation from './BikeElevation'
import { getDelayColorClass } from './RdcSection/DelayTime/DelayTime'
import RdcSection, { RdcSectionDeparture, RdcSectionTransportMode } from './RdcSection/RdcSection'
import { SectionBreakdownItem } from './RdcSection/SectionBreakdownList/SectionBreakdownList'
import RoutePathInfo, { fromRdcSectionToRouteListItemSection } from './RoutePathInfo'

interface RdcProps {
  destinationWaypoint: RouteWaypoint
  route: StateRoute
}

export interface Section {
  name?: string
  mode: RdcSectionTransportMode
  headsign?: string
  color: ColorOptions
  delay?: number
  duration: number
  distance: number
  departure: RdcSectionDeparture
  breakdownItems: SectionBreakdownItem[]
}

export interface ColorOptions {
  text: string
  icon: string
  background: string
}

export default function Rdc({ destinationWaypoint, route }: RdcProps): JSX.Element {
  const dispatch = useAppDispatch()
  const sections = route.sections.map(toRdcSection)
  const transportMode = useAppSelector(({ routePlanner }) => routePlanner.selectedTransportMode)
  const countryCode = useAppSelector((state) => state.user.countryCode)
  const unit = useAppSelector((state) => state.app.unit)
  const delayColorClass = getDelayColorClass(route.duration, route.delay, 'Rdc__delay--traffic-')
  const delayTranslation =
    route.delay !== undefined && route.delay >= 60
      ? i18n('route.time.with.traffic', [formatLocalTimeDuration(route.delay)])
      : undefined
  const formattedDistance = formatDistance(route.distance, { countryCode, unit })
  const singleTransport = route.sections.length === 1 ? route.sections[0] : undefined
  const showElevation =
    transportMode === TransportModesPanelMode.Bike &&
    route.elevationSections &&
    route.elevationSections?.length > 0 &&
    !(route.elevationInfo?.elevation_down === 0 && route.elevationInfo.elevation_up === 0)
  const destinationAddress = useAppSelector(({ search }: RootState) =>
    search.locationAddress !== undefined &&
    search.locationAddress.length > 0 &&
    search.locationAddress[0] !== undefined
      ? search.locationAddress[0]?.address?.label
      : ''
  )

  const handleOnClose = (): void => {
    console.debug('Remove dispatch action from here')
    dispatch(setSelectedRoute())
  }

  return (
    <div className="Rdc">
      <div className="Rdc__header-row">
        <section className="Rdc__header">
          <h4 className="Rdc__title">{formatLocalTimeDuration(route.duration)}</h4>
          {route.delay !== undefined && (
            <span className={`Rdc__delay ${delayColorClass}`}>{delayTranslation}</span>
          )}
        </section>
        <CloseButton onClick={handleOnClose} />
      </div>
      {/*TODO Create a chp breadcrumb component for this caus it's duplicate */}
      <div className="Rdc__distance-mode-details-row hds-margin-b-4">
        {singleTransport !== undefined && (
          <div className="Rdc__distance-lable">{formattedDistance}</div>
        )}
        <RoutePathInfo
          sections={sections.map((section) => fromRdcSectionToRouteListItemSection(section))}
          transportMode={transportMode}
        />
      </div>

      <div className="Rdc__buttons-row">
        <HDSButton size="medium" type="button" variant="primary" width="100%">
          Push to Phone
        </HDSButton>

        <HDSButton icon="share-android" size="medium" type="button" variant="secondary" />

        <HDSButton icon="star-empty" size="medium" type="button" variant="secondary" />
      </div>
      <ScrollableContainer>
        <>
          <div className="Rdc__ListItems-container">
            {showElevation === true && (
              <BikeElevation
                elevationSections={route.elevationSections ?? []}
                distance={route.distance}
                elevationInfo={route.elevationInfo}
                countryAndUnit={{ countryCode, unit }}
              />
            )}
            {sections.map((section: Section, index: number) => (
              <RdcSection
                className="Rdc__section"
                breakdownItems={section.breakdownItems}
                delay={section.delay}
                departure={section.departure}
                distance={section.distance}
                duration={section.duration}
                transport={{
                  color: section.color.background,
                  headsign: section.headsign,
                  mode: section.mode,
                  name: section.name,
                  textColor: section.color.text,
                }}
                key={index}
                isCollapsible={sections.length > 1 && section.breakdownItems.length > 1}
                countryAndUnit={{ countryCode, unit }}
              />
            ))}
            <ArrivalSection
              routeWaypoint={destinationWaypoint}
              time={new Date(route.arrival)}
              address={destinationAddress}
            />
          </div>
          {route.attributions && route.attributions?.length > 0 && (
            <div className="Rdc__disclaimer-section">
              <div className="Rdc__disclaimer-message">
                {i18n('route.public-transportation.disclaimer')}
              </div>
              {route.attributions.map((attributions: StateRouteAttributions, index: number) => (
                <a
                  key={`attributions-${index}`}
                  href={attributions.href}
                  target="_blank"
                  className="Rdc__link"
                  rel="noreferrer"
                >
                  {attributions.text}
                </a>
              ))}
            </div>
          )}
        </>
      </ScrollableContainer>
    </div>
  )
}
